import type { AuthSession, ImpersonationPurposeCode } from '../api/types';
import {
  fixtureImpersonationTargets,
  type ImpersonationTarget,
} from '../features/security/public';

const fixtureSession: AuthSession = {
  principal: {
    type: 'human', tenant: 'heyday', personId: 'person-fixture', workerId: '03',
    roles: ['HEYDAY_ADMIN', 'People & Culture', 'Manager'], scopes: ['staffing.read', 'staffing.write', 'directory.read', 'lifecycle.read', 'lifecycle.write', 'person.read', 'person.write', 'business_process.read', 'business_process.approve', 'absence.read', 'absence.write', 'absence.manage', 'rewards.read', 'rewards.write', 'headcount.read', 'headcount.write', 'documents.read', 'documents.write', 'certificates.request.self', 'certificates.read.self', 'certificates.operations.read', 'certificates.issue', 'certificates.revoke', 'certificates.settings.read', 'certificates.settings.write', 'workflow.read', 'workflow.write', 'workflow.run', 'workflow.template.read', 'workflow.template.write', 'workflow.template.publish', 'workflow.request.create', 'workflow.request.read.self', 'workflow.request.approve', 'workflow.request.cancel', 'workflow.request.comment', 'workflow.request.read.all', 'workflow.request.export', 'bulk.read', 'bulk.write', 'bulk.run', 'reports.read', 'reports.export', 'recruiting.read', 'recruiting.write', 'security.read', 'security.manage', 'security.impersonate', 'tenant.settings.read', 'tenant.settings.write'],
  },
  csrfToken: 'fixture-csrf-token',
  expiresAt: '2099-12-31T23:59:59Z',
};

const fixtureImpersonationStorageKey = 'heyday.fixture.impersonation';

type FixtureImpersonationRecord = {
  targetIdentityId: string;
  purposeCode: ImpersonationPurposeCode;
  startedAt: string;
  expiresAt: string;
};

export function storeFixtureImpersonation(target: ImpersonationTarget, purposeCode: ImpersonationPurposeCode) {
  const startedAt = new Date();
  const record: FixtureImpersonationRecord = {
    targetIdentityId: target.identityId,
    purposeCode,
    startedAt: startedAt.toISOString(),
    expiresAt: new Date(startedAt.getTime() + 15 * 60 * 1000).toISOString(),
  };
  window.sessionStorage.setItem(fixtureImpersonationStorageKey, JSON.stringify(record));
}
export function clearFixtureImpersonation() {
  window.sessionStorage.removeItem(fixtureImpersonationStorageKey);
}

export function fixtureSessionFromStorage(): AuthSession {
  try {
    const raw = window.sessionStorage.getItem(fixtureImpersonationStorageKey);
    if (!raw) return fixtureSession;
    const record = JSON.parse(raw) as FixtureImpersonationRecord;
    const target = fixtureImpersonationTargets.find((candidate) => candidate.identityId === record.targetIdentityId);
    const validPurpose = ['SUPPORT', 'ACCESS_VERIFICATION', 'TROUBLESHOOTING'].includes(record.purposeCode);
    if (!target || !validPurpose || !record.startedAt || !record.expiresAt || new Date(record.expiresAt).getTime() <= Date.now()) {
      clearFixtureImpersonation();
      return fixtureSession;
    }
    return {
      principal: {
        type: 'human', tenant: fixtureSession.principal.tenant, personId: target.personId, workerId: target.workerId,
        roles: ['Employee'], scopes: ['absence.read', 'documents.read', 'certificates.request.self', 'certificates.read.self'],
      },
      csrfToken: 'fixture-impersonation-csrf-token',
      expiresAt: record.expiresAt,
      impersonation: {
        id: 'fixture-impersonation', purposeCode: record.purposeCode, startedAt: record.startedAt, expiresAt: record.expiresAt, readOnly: true,
        actor: { personId: fixtureSession.principal.personId, workerId: fixtureSession.principal.workerId },
      },
    };
  } catch {
    clearFixtureImpersonation();
    return fixtureSession;
  }
}
